/**
 * Корневой компонент.
 *
 * Первый законченный сценарий (ТЗ §16.3): гость открывает сайт, получает
 * локальное пространство, выбирает язык и раскладку, проходит диагностику,
 * получает персональную тренировку, видит результат, регистрируется и
 * переносит прогресс. Логика переходов живёт в useAppFlow, разметка — в
 * ScreenRouter.
 */

import { useEffect, useMemo, useRef } from 'react';
import { createTranslate, getLocale, type Translate } from '@type2x/i18n';
import { Button } from '@type2x/ui';
import { DEFAULT_APPEARANCE, type Appearance } from '@type2x/feature-personal-workspace';

import { createRuntime } from './runtime.js';
import { useWorkspace } from './hooks/useWorkspace.js';
import { useAppFlow, type AppFlow } from './hooks/useAppFlow.js';
import { useAppUpdate } from './hooks/useAppUpdate.js';
import { useTheme } from './hooks/useTheme.js';
import { ScreenRouter } from './screens/ScreenRouter.js';
import { SettingsPanel } from './screens/SettingsPanel.js';
import { AccountStatusButton } from './screens/AccountStatusButton.js';
import { useAccountStatus, type AccountStatus } from './hooks/useAccountStatus.js';
import { FocusModeContext, focusApplies } from './screens/focus-mode.js';
import { PlatformContext } from './screens/platform.js';
import { KeyboardPresenceContext, useDetectedKeyboard } from './screens/keyboard-presence.js';
import { SoundOnErrorContext } from './screens/sound.js';

/**
 * Экраны, на которых приложение ещё представляется.
 *
 * Только здесь под названием стоит подзаголовок: на экране занятия он
 * рассказывал человеку, чем тот уже занят, и стоил строки.
 */
const INTRO_SCREENS = new Set(['loading', 'onboarding', 'language-setup', 'home']);

/**
 * Кнопка входа в фокус-режим.
 *
 * Имя нужно, чтобы вернуть ей фокус после выхода (ТЗ UI-48): человек,
 * работающий с клавиатуры, обязан оказаться там же, откуда уходил, а не в
 * начале страницы.
 */
const FOCUS_CONTROL_ID = 'focus-control';

/**
 * Шапка приложения: название, переходы, состояние входа, фокус и настройки.
 *
 * Одна строка постоянной высоты — 64 пикселя, 56 на телефоне (ТЗ UI-35).
 * Раньше здесь стояли подряд название с подзаголовком, переходы, язык
 * тренировки и пять кнопок внешнего вида: ряд, который на экране занятия
 * отодвигал текст упражнения за нижний край. Теперь постоянное место в шапке
 * есть только у того, чем пользуются во время занятия, — у выхода на главную,
 * переходов, состояния входа и фокус-режима; остальное открывается кнопкой
 * «Настройки».
 *
 * «Фокус» показывается только там, где печатают (ТЗ UI-35): на экране отчёта
 * убирать нечего, а кнопка, которая ничего не меняет, учит не верить кнопкам.
 *
 * Подзаголовок остался там, где приложение представляется, — на первом входе и
 * на главной. На экране занятия он объяснял человеку, чем тот уже занят.
 *
 * В фокус-режиме от шапки остаётся только выход из него: название пользователь
 * уже прочитал, а текст упражнения — нет.
 */
function AppHeader({
  t,
  flow,
  appearance,
  focusMode,
  accountStatus,
}: {
  readonly t: Translate;
  readonly flow: AppFlow;
  readonly appearance: Appearance;
  /** Действует ли фокус-режим здесь: на экранах без набора он отступает. */
  readonly focusMode: boolean;
  readonly accountStatus: AccountStatus;
}): React.JSX.Element {
  if (focusMode) {
    return (
      <header className="t2x-header">
        <Button variant="ghost" onClick={() => flow.onFocusModeChange(false)}>
          {t('appearance.focusOff')}
        </Button>
      </header>
    );
  }

  // На экране первого входа в новый язык выбранным показан именно он: экран
  // говорит о нём, а профиль его ещё не заведён, поэтому активным в
  // пространстве остаётся прежний. Отмечать в этот момент прежний значит
  // спорить с тем, что человек только что нажал (ТЗ FR-EN-87).
  const shownLanguage =
    flow.screen.name === 'language-setup' ? flow.screen.language : flow.trainingLanguage;

  return (
    <header className="t2x-header">
      <div className="t2x-header__brand">
        {/* Название — выход на главную (ТЗ FR-EN-56): так устроен любой сайт,
            и искать отдельную кнопку «домой» человек не должен. До загрузки
            пространства вести некуда, и название остаётся названием. */}
        <h1>
          {flow.trainingLanguage === null ? (
            t('app.title')
          ) : (
            <button type="button" className="t2x-brand" onClick={flow.onOpenHome}>
              {t('app.title')}
            </button>
          )}
        </h1>
        {INTRO_SCREENS.has(flow.screen.name) && (
          <p className="t2x-muted t2x-header__tagline">{t('app.tagline')}</p>
        )}
      </div>

      <NavButtons t={t} flow={flow} />

      <div className="t2x-header__tools">
        <AccountStatusButton t={t} status={accountStatus} onOpenAccount={flow.onSaveProgress} />

        {/* Фокус-режим включают посреди занятия, поэтому он остаётся кнопкой,
            а не пунктом раскрываемого диалога. На экранах без набора его нет:
            убирать там нечего (ТЗ UI-35). */}
        {focusApplies(flow.screen) && (
          <Button
            id={FOCUS_CONTROL_ID}
            variant="ghost"
            aria-label={t('appearance.focusOn')}
            onClick={() => flow.onFocusModeChange(true)}
          >
            <span className="t2x-header__label">{t('appearance.focusOn')}</span>
          </Button>
        )}

        <SettingsPanel
          t={t}
          theme={appearance.theme}
          soundOnError={appearance.soundOnError}
          gameElements={appearance.gameElements}
          trainingLanguage={shownLanguage}
          onThemeChange={flow.onThemeChange}
          onSoundOnErrorChange={flow.onSoundOnErrorChange}
          onGameElementsChange={flow.onGameElementsChange}
          onTrainingLanguageChange={flow.onTrainingLanguageChange}
        />
      </div>
    </header>
  );
}

/**
 * Переходы назад и вперёд.
 *
 * «Назад» есть на каждом экране, кроме первого входа и самой главной: уйти из
 * занятия иначе было бы нечем — только перезагрузкой страницы. «Вперёд»
 * появляется только после «Назад»: кнопка, которой некуда вести, бесполезна и
 * сбивает с толку.
 */
function NavButtons({
  t,
  flow,
}: {
  readonly t: Translate;
  readonly flow: AppFlow;
}): React.JSX.Element | null {
  if (!flow.canGoBack && !flow.canGoForward) return null;

  return (
    <div className="t2x-nav" role="group" aria-label={t('nav.label')}>
      {flow.canGoBack && (
        <Button variant="ghost" onClick={flow.onBack}>
          {t('nav.back')}
        </Button>
      )}
      {flow.canGoForward && (
        <Button variant="ghost" onClick={flow.onForward}>
          {t('nav.forward')}
        </Button>
      )}
    </div>
  );
}

/**
 * Сообщение о готовой новой версии.
 *
 * Вынесено из корневого компонента: с ним тот перерос порог длины, за которым
 * его перестают читать целиком (ТЗ NFR-Q-02).
 */
function UpdateNotice({
  t,
  update,
}: {
  readonly t: Translate;
  readonly update: ReturnType<typeof useAppUpdate>;
}): React.JSX.Element | null {
  if (!update.ready) return null;

  return (
    <p className="t2x-update">
      {t('update.available')}{' '}
      <Button variant="ghost" onClick={update.apply}>
        {t('update.apply')}
      </Button>
    </p>
  );
}

/**
 * Выход из фокус-режима по Escape и возврат фокуса кнопке (ТЗ UI-48).
 *
 * Обработчик живёт только пока режим включён: в обычном состоянии в пути
 * нажатия не появляется ничего лишнего (ТЗ UI-78). Escape — общий для
 * «закрыть то, что открыли», и его ищут первым.
 */
function useFocusModeExit(focusMode: boolean, onLeave: () => void): void {
  const wasFocused = useRef(focusMode);

  useEffect(() => {
    if (!focusMode) return undefined;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onLeave();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    // Кнопки нет там, где не печатают: тогда возвращать фокус некуда, и
    // ничего не происходит.
    if (wasFocused.current && !focusMode) document.getElementById(FOCUS_CONTROL_ID)?.focus();
    wasFocused.current = focusMode;
  }, [focusMode]);
}

export function App(): React.JSX.Element {
  const runtime = useMemo(() => createRuntime(), []);
  const access = useWorkspace(runtime);
  const flow = useAppFlow(runtime, access);

  const locale = getLocale(access.workspace?.uiLanguage ?? 'ru');
  const t = useMemo(() => createTranslate(locale.messages), [locale]);

  const appearance = access.workspace?.appearance ?? DEFAULT_APPEARANCE;
  useTheme(access.workspace === null ? null : appearance.theme);

  const update = useAppUpdate();

  // Фокус-режим действует там, где печатают, и отступает на остальных экранах
  // (ТЗ FR-EN-86). Настройка при этом остаётся включённой: человек включал
  // режим один раз, а не для каждого упражнения.
  const focusMode = appearance.focusMode && focusApplies(flow.screen);

  // Состояние входа видно на каждом экране, кроме фокус-режима (ТЗ UI-60).
  const account = useAccountStatus(flow.onReadSession, flow.verification, flow.screen.name);

  useFocusModeExit(focusMode, () => flow.onFocusModeChange(false));

  // Чем печатают на этом устройстве: телефон без клавиатуры тренировать не
  // может, и молчать об этом — значит показывать сломанный экран.
  const keyboard = useDetectedKeyboard();

  return (
    <KeyboardPresenceContext.Provider value={keyboard}>
      <PlatformContext.Provider value={flow.platform}>
        <SoundOnErrorContext.Provider value={appearance.soundOnError}>
          <FocusModeContext.Provider value={focusMode}>
            <main className={focusMode ? 't2x-shell t2x-shell--focus' : 't2x-shell'}>
              {/* Сообщение о новой версии, но не перезагрузка: решает человек, и
                не посреди упражнения. В фокус-режиме не показывается — там на
                экране только текст упражнения. */}
              {!focusMode && <UpdateNotice t={t} update={update} />}

              {/*
              О входе и выходе сказано вслух (ТЗ UI-48): экран меняется
              целиком, а человек, который его не видит, иначе не узнает, что
              панели убрались. Область живая, но меняется только по нажатию —
              не на каждую букву, как это было у метрик.
            */}
              <p className="t2x-visually-hidden" role="status">
                {focusMode ? t('appearance.focusEntered') : ''}
              </p>

              <AppHeader
                t={t}
                flow={flow}
                appearance={appearance}
                focusMode={focusMode}
                accountStatus={account.status}
              />

              {/*
              Ключ по имени экрана: новый экран — новый узел, и однократное
              появление проигрывается ровно на переходе (ТЗ UI-72). Внутри
              одного экрана ключ не меняется, поэтому занятие не пересоздаётся
              и измерение не начинается заново.
            */}
              <div className="t2x-screen" key={flow.screen.name}>
                <ScreenRouter
                  runtime={runtime}
                  t={t}
                  screen={flow.screen}
                  onOnboardingDone={flow.onOnboardingDone}
                  onDiagnosticsDone={flow.onDiagnosticsDone}
                  onContinueAfterDiagnostics={flow.onContinueAfterDiagnostics}
                  onRetakeDiagnostics={flow.onRetakeDiagnostics}
                  onPracticeDone={flow.onPracticeDone}
                  onAgain={flow.onAgain}
                  onSaveProgress={flow.onSaveProgress}
                  onRegister={flow.onRegister}
                  onLogin={flow.onLogin}
                  onReadSession={flow.onReadSession}
                  onResendVerification={flow.onResendVerification}
                  onRequestPasswordReset={flow.onRequestPasswordReset}
                  onExportProgress={flow.onExportProgress}
                  onDeleteAccount={flow.onDeleteAccount}
                  onLogout={flow.onLogout}
                  onConfirmPasswordReset={flow.onConfirmPasswordReset}
                  onConsentChange={flow.onConsentChange}
                  verification={flow.verification}
                  onContinueAfterVerification={flow.onContinueAfterVerification}
                  consent={access.workspace?.privacy.leaderboardOptIn ?? false}
                  onStartControlTest={flow.onStartControlTest}
                  onControlTestDone={flow.onControlTestDone}
                  onBackToPractice={flow.onBackToPractice}
                  retentionDue={flow.retentionDue}
                  resumesToFundamentals={flow.resumesToFundamentals}
                  onStartMistakes={flow.onStartMistakes}
                  onMistakeStageDone={flow.onMistakeStageDone}
                  onStartSentences={flow.onStartSentences}
                  onSentenceDone={flow.onSentenceDone}
                  onStartFundamentals={flow.onStartFundamentals}
                  onFundamentalsDone={flow.onFundamentalsDone}
                  onStartSymbols={flow.onStartSymbols}
                  onSymbolsDone={flow.onSymbolsDone}
                  onStartPacks={flow.onStartPacks}
                  onPacksDone={flow.onPacksDone}
                  onOpenReport={flow.onOpenReport}
                  onOpenAchievements={flow.onOpenAchievements}
                  onOpenLeaderboard={flow.onOpenLeaderboard}
                  trainingLanguage={flow.trainingLanguage}
                  gameElements={appearance.gameElements}
                  onStartLayoutSwitch={flow.onStartLayoutSwitch}
                  onLayoutSwitchDone={flow.onLayoutSwitchDone}
                  onStartTempo={flow.onStartTempo}
                  onTempoRunDone={flow.onTempoRunDone}
                  onStartCustomText={flow.onStartCustomText}
                  onCustomTextSubmit={flow.onCustomTextSubmit}
                  onCustomTextDone={flow.onCustomTextDone}
                  symbolModes={flow.symbolModes}
                  packModes={flow.packModes}
                  fundamentalsAvailable={flow.fundamentalsAvailable}
                  mistakesAvailable={flow.mistakesAvailable}
                  latin={flow.latin}
                  layoutSwitch={flow.layoutSwitch}
                  keys={flow.keys}
                  onLanguageSetupDone={flow.onLanguageSetupDone}
                  onLanguageSetupCancel={flow.onLanguageSetupCancel}
                  onTrainingLanguageChange={flow.onTrainingLanguageChange}
                  startPath={flow.startPath}
                  onStartPathChange={flow.onStartPathChange}
                  onProceedLocked={flow.onProceedLocked}
                  onBack={flow.onBack}
                />
              </div>
            </main>
          </FocusModeContext.Provider>
        </SoundOnErrorContext.Provider>
      </PlatformContext.Provider>
    </KeyboardPresenceContext.Provider>
  );
}
